// Public Team Cup leaderboard. Used by /leaderboard and the /tv/scores
// public displays when the active tournament's format === 'Team Cup'.

import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { watchTeamCup } from '../lib/teamCupData.js'
import {
  computeTeamMatchResults, computeScatPayouts, computeCthValues,
  strokesForRound, DEFAULT_STROKE_INDEX,
} from '../lib/teamCupScoring.js'
import { calcCourseHandicap, calcPlayingHandicap } from '../lib/handicap.js'
import { TV_BG_GREEN } from '../config/tvTheme.js'

export default function TeamCupLeaderboard({ tournament, tournamentId, pars, players, tv = false }) {
  const info = tournament?.info || {}
  const config = info.teamCupConfig || {}
  const [data, setData] = useState({})
  useEffect(() => {
    const unsub = watchTeamCup(tournamentId, setData)
    return () => unsub()
  }, [tournamentId])

  const teams = entriesAsList(data?.teams)
  const groups = entriesAsList(data?.groups)
  const scoresByGroup = data?.scores || {}
  const cthSubs = data?.cth || {}
  const isFinal = data?.status === 'final'

  // Handicaps + stroke allocation
  const handicapsOn = !!config.handicapsOn && (info.handicapMode && info.handicapMode !== 'none')
  const slopeRating = Number(info?.slopeRating) || 113
  const courseRating = Number(info?.courseRating) || (Number(info?.coursePar) || 72)
  const coursePar = Number(info?.coursePar) || 72
  const allowance = Number(info?.handicapAllowance) || 100
  const courseHandicaps = useMemo(() => {
    const out = {}
    if (!handicapsOn) return out
    for (const p of players) {
      const hi = Number(p.handicapIndex)
      if (!Number.isFinite(hi)) continue
      const ch = calcCourseHandicap(hi, slopeRating, courseRating, coursePar)
      out[p.id] = calcPlayingHandicap(ch, allowance)
    }
    return out
  }, [handicapsOn, players, slopeRating, courseRating, coursePar, allowance])
  const strokesByPlayer = useMemo(
    () => strokesForRound(courseHandicaps, DEFAULT_STROKE_INDEX),
    [courseHandicaps]
  )

  // Per-group results
  const groupResults = useMemo(() => groups.map(g => {
    const gids = new Set(g.playerIds || [])
    const groupTeams = teams
      .map(t => ({
        id: t.id, name: t.name || 'Team',
        playerIds: (t.playerIds || []).filter(pid => gids.has(pid)),
      }))
      .filter(t => t.playerIds.length > 0)
    const res = computeTeamMatchResults({
      teams: groupTeams,
      scoresByHole: scoresByGroup?.[g.id] || {},
      strokesByPlayer, handicapsOn,
      scoringSystem: config.scoringSystem || 'official',
    })
    return { group: g, teams: groupTeams, ...res }
  }), [groups, teams, scoresByGroup, strokesByPlayer, handicapsOn, config.scoringSystem])

  // Aggregate team totals
  const teamTotals = useMemo(() => {
    const out = {}
    teams.forEach(t => { out[t.id] = 0 })
    groupResults.forEach(gr => {
      Object.entries(gr.teamPoints).forEach(([tid, pts]) => {
        if (out[tid] != null) out[tid] += pts
      })
    })
    return out
  }, [groupResults, teams])

  const allPlayerIds = useMemo(
    () => Array.from(new Set(groups.flatMap(g => g.playerIds || []))),
    [groups]
  )
  const scats = useMemo(() => config.scatsEnabled
    ? computeScatPayouts({
        playerIds: allPlayerIds, scoresByGroup,
        perHole: Number(config.scatsPerHole) || 0, roundFinal: isFinal,
      })
    : null, [config.scatsEnabled, allPlayerIds, scoresByGroup, config.scatsPerHole, isFinal])

  const cth = useMemo(() => config.cthEnabled
    ? computeCthValues({
        pars: pars || [], cthByHole: cthSubs,
        groupCodes: groups.map(g => g.id), perPar3: Number(config.cthPerPar3) || 0,
      })
    : null, [config.cthEnabled, pars, cthSubs, groups, config.cthPerPar3])

  const playerName = (pid) => (players.find(p => p.id === pid)?.name) || pid
  const ranked = [...teams].sort((a, b) => (teamTotals[b.id] || 0) - (teamTotals[a.id] || 0))

  return (
    <div className="min-h-full" style={{ backgroundColor: tv ? TV_BG_GREEN : 'var(--ge-primary)', color: '#FFFFFF' }}>
      <header className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <button onClick={() => window.history.back()} className="text-xs font-bold uppercase tracking-widest text-amber-300">← Back</button>
        <div className="text-center flex-1 mx-2">
          <div className="text-xs uppercase tracking-widest opacity-70">Team Cup</div>
          <div className="font-bold text-base">{info.name || 'Tournament'}</div>
          <div className="text-xs opacity-80">
            {config.scoringSystem === 'custom' ? 'Custom Scoring' : 'Official Team Cup Scoring'}
            {handicapsOn ? ' · NET' : ''}
            {isFinal ? ' · FINAL' : ' · LIVE'}
          </div>
        </div>
        <Link to="/" className="text-xs font-bold uppercase tracking-widest text-amber-300">Home</Link>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        {/* Team standings */}
        <Section title="Team Standings">
          {teams.length === 0 ? (
            <div className="text-sm opacity-70 italic">No teams configured.</div>
          ) : (
            <div className="space-y-2">
              {ranked.map((t, i) => (
                <div key={t.id} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs opacity-60 w-5">{i + 1}.</span>
                    <span className="font-bold text-lg">{t.name || 'Team'}</span>
                  </div>
                  <span className="text-3xl font-extrabold text-amber-300">{fmtPts(teamTotals[t.id] || 0)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Per-group */}
        <Section title="Groups">
          {groupResults.length === 0 ? (
            <div className="text-sm opacity-70 italic">No groups yet.</div>
          ) : (
            <div className="space-y-2">
              {groupResults.map(gr => (
                <div key={gr.group.id} className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono font-bold text-amber-300 text-lg">{gr.group.code}</span>
                    <span className="text-xs opacity-70">{gr.holes.filter(h => h.winnerId != null).length}/18 holes</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-3 text-sm">
                    {gr.teams.map(t => (
                      <span key={t.id}>
                        <span className="opacity-70">{t.name}: </span>
                        <span className="font-bold">{fmtPts(gr.teamPoints[t.id] || 0)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Scats */}
        {scats ? (
          <Section title={`Scats${isFinal ? ' — Settled' : ' — Running'}`}>
            <div className="text-xs opacity-70 mb-2">
              Low gross across all players · ${config.scatsPerHole}/hole · ties cancel
            </div>
            <table className="w-full text-sm">
              <thead className="opacity-60"><tr><th className="text-left py-1">Player</th><th className="text-right py-1">Holes</th><th className="text-right py-1">{isFinal ? '$' : ''}</th></tr></thead>
              <tbody>
                {Object.entries(scats.holesWonByPlayer)
                  .sort((a, b) => b[1] - a[1])
                  .filter(([, n]) => n > 0 || isFinal)
                  .map(([pid, n]) => {
                    const pay = scats.payoutsByPlayer[pid] || 0
                    return (
                      <tr key={pid} className="border-t border-white/10">
                        <td className="py-1">{playerName(pid)}</td>
                        <td className="text-right py-1 font-bold">{n}</td>
                        <td className={`text-right py-1 font-bold ${pay >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                          {isFinal ? formatMoney(pay) : ''}
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          </Section>
        ) : null}

        {/* CTH */}
        {cth ? (
          <Section title="Closest to Hole">
            <div className="text-xs opacity-70 mb-2">Par 3 · ${config.cthPerPar3}/pot · carry forward when none</div>
            <table className="w-full text-sm">
              <thead className="opacity-60"><tr><th className="text-left py-1">Hole</th><th className="text-left py-1">Winner</th><th className="text-right py-1">Paid</th></tr></thead>
              <tbody>
                {cth.perHole.map(r => (
                  <tr key={r.hole} className="border-t border-white/10">
                    <td className="py-1">{r.hole}</td>
                    <td className="py-1">{r.winnerId === 'none' ? 'None (carry)' : r.winnerId ? playerName(r.winnerId) : '—'}</td>
                    <td className="text-right py-1 font-bold">{r.paid > 0 ? formatMoney(r.paid) : (r.carryFromHere > 0 ? `→ ${formatMoney(r.carryFromHere)}` : '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {cth.leftoverBucket > 0 ? (
              <div className="text-xs text-amber-300 font-bold mt-2">Unclaimed pot: {formatMoney(cth.leftoverBucket)}</div>
            ) : null}
          </Section>
        ) : null}
      </main>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="text-xs font-bold uppercase tracking-widest opacity-70 mb-2">{title}</h3>
      {children}
    </section>
  )
}

function entriesAsList(obj) {
  return Object.entries(obj || {}).map(([id, v]) => ({ id, ...v }))
}
function fmtPts(n) {
  if (Number.isInteger(n)) return String(n)
  return String(Math.round(n * 10) / 10)
}
function formatMoney(n) {
  const v = Number(n) || 0
  const sign = v >= 0 ? '+' : '-'
  return `${sign}$${Math.abs(v).toFixed(2)}`
}
